/* WenStorm analytics — a platform-neutral event layer.
 *
 * SHOPIFY IS ONE FRONTEND, NOT THE MODEL. Everything below is written so that when WenStorm leaves
 * Shopify, the event vocabulary, the property names and the PostHog project all survive unchanged
 * and only the adapter at the bottom is replaced. That is why there are no Shopify words in the
 * event names, and why no Liquid handle or Shopify GID is used as an analytics identity.
 *
 *     Shopify storefront  ─┐
 *                          ├─> WenStormAnalytics ──> provider (PostHog today) ──> PostHog project
 *     future frontend     ─┘
 *
 * The alternative -- posthog.capture() sprinkled through Liquid sections -- would make the move
 * expensive and would split the history into incompatible generations of event names at the
 * boundary. `page_viewed { page_type: 'forum_thread' }` asked in 2026 is the same question in 2028.
 *
 * NOTHING HERE IMPORTS POSTHOG. The provider is injected by the bootstrap, so this file can be
 * unit-tested, and a second provider can exist without touching the event layer.
 *
 * WHAT SURVIVES A FRONTEND MIGRATION, PRECISELY: the CONTRACT does -- event names, property names,
 * page_instance_id semantics, the three engagement numbers and the PostHog history built from them.
 * Not every line of the implementation. Today's storefront does full page loads, so one document is
 * one page instance and the lifecycle below can bind to pagehide. A single-page frontend navigates
 * without unloading the document, and must instead call startPageInstance() on each route change,
 * which finalizes the outgoing instance and opens a new one. That entry point exists for exactly
 * that reason. Claiming the file itself never changes would have been wrong.
 */
(function (global) {
  'use strict';

  /* THE VOCABULARY, and the last word on it: anything not listed here is recorded as 'other'.
   *
   * That coercion is deliberate -- a frontend must not be able to invent a category that no report
   * knows about -- but it makes this list a SECOND place the taxonomy is defined, and the two can
   * drift silently. They did: four categories were added to the Liquid adapter and every one of
   * them arrived here, failed this check, and was filed as 'other'. The classifier looked correct
   * in the template, the assertions passed against the template, and the data never changed.
   *
   * So: adding a category means adding it in BOTH places, and scripts/test-page-taxonomy.mjs now
   * compares the two lists directly rather than trusting either one alone. */
  var PAGE_TYPES = ['homepage', 'forum_home', 'forum_topic', 'forum_thread', 'forum_profile',
                    'product', 'collection', 'cart', 'checkout', 'article', 'search',
                    // Added with the page-centric view: the categories the dashboard navigates by.
                    // 'landing' is every /pages/* that is not a forum surface -- the pages most
                    // worth diagnosing, and previously indistinguishable from miscellany.
                    'landing', 'blog_index', 'policy', 'not_found', 'other'];

  function WenStormAnalytics() {
    this._provider = null;
    this._context = {};
    this._queue = [];
    this._enabled = false;
    this._failures = 0;
    this._pageInstanceId = null;
    this._engagement = null;
  }

  function mintId(prefix) {
    return prefix + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8);
  }

  /* PAGE INSTANCE IDENTITY -- minted here, not inside the engagement tracker.
   *
   * It used to be created by startEngagement(), which ran AFTER pageViewed(). The page_viewed event
   * therefore carried no page_instance_id, and the repository would have had to re-join a page view
   * to its own engagement record by (window_id, URL, timestamp proximity) -- a heuristic, for an
   * identity we already had in hand. Now every event on a page carries the same instance id and the
   * join is an equality.
   *
   * Calling it again finalizes the previous instance and opens a new one, which is what a
   * client-side route change in a future frontend needs; today it is called once at bootstrap. */
  WenStormAnalytics.prototype.startPageInstance = function () {
    if (this._engagement && !this._engagement.isFinal()) this._engagement.finalize('page_instance_replaced');
    this._pageInstanceId = mintId('pi_');
    this._engagement = null;
    // A new page instance is a new measurement, so it re-reads the document. Within an instance the
    // list never changes again -- see _trackedHeadings().
    this._headings = null;
    this._applyGlobalProperties();
    return this._pageInstanceId;
  };

  /* GLOBAL PROPERTIES -- how the page instance reaches events we do not emit ourselves.
   *
   * The transport also captures raw interaction events of its own, which never pass through _send()
   * and so carry none of our properties. Those raw rows are wanted: they are the coverage of every
   * click on things we deliberately do not model -- a gallery image, a spec accordion, a filter.
   * But the timeline has to know WHICH page visit each one belongs to, and URL is not an answer:
   * the same visitor can open the same product page twice in one session, and those are two
   * visits with two instance ids.
   *
   * So the current instance id is handed to the transport as a global property, and the transport
   * decides how to attach it to everything it sends. 'Global properties' is a transport concept,
   * not a vendor one; a provider that cannot do it simply does not implement the method and the
   * repository falls back to assigning raw rows by page-view boundaries within a tab. */
  WenStormAnalytics.prototype._applyGlobalProperties = function () {
    var p = this._provider;
    if (!p || typeof p.setGlobalProperties !== 'function') return false;
    try { return p.setGlobalProperties({ page_instance_id: this._pageInstanceId }) !== false; }
    catch (e) { this._failures++; return false; }
  };

  WenStormAnalytics.prototype.pageInstanceId = function () { return this._pageInstanceId; };

  /* The context every event carries. Supplied by the frontend adapter -- Liquid today -- and never
   * derived from the URL here, because a URL shape is a frontend detail and this layer is meant to
   * outlive the current one. */
  WenStormAnalytics.prototype.setContext = function (ctx) {
    ctx = ctx || {};
    var pageType = PAGE_TYPES.indexOf(ctx.page_type) >= 0 ? ctx.page_type : 'other';
    this._context = {
      page_type: pageType,
      // A durable WenStorm identifier where one exists -- NOT a Shopify GID and not a Liquid handle.
      // The forum's own post id survives a storage migration; gid://shopify/Metaobject/… does not.
      entity_type: ctx.entity_type || null,
      entity_id: ctx.entity_id == null ? null : String(ctx.entity_id),
      // A handle is useful for reading reports by eye, so it is kept ALONGSIDE the id, never instead.
      entity_handle: ctx.entity_handle || null,
      topic: ctx.topic || null,
      content_source: ctx.content_source || null,
      route: ctx.route || null,
    };
    return this;
  };

  WenStormAnalytics.prototype.dispatchFailures = function () { return this._failures; };
  // Kept as an alias so existing probes do not break; prefer dispatchFailures().
  WenStormAnalytics.prototype.failures = function () { return this._failures; };

  WenStormAnalytics.prototype.getContext = function () {
    var c = {}; for (var k in this._context) if (this._context.hasOwnProperty(k)) c[k] = this._context[k];
    return c;
  };

  /* Collection is OFF until a provider is installed AND enable() is called. The bootstrap decides;
   * this layer never decides for itself whether it may run. */
  WenStormAnalytics.prototype.use = function (provider) {
    this._provider = provider;
    // The instance may already be open when the provider arrives; do not let it miss the handover.
    if (this._pageInstanceId) this._applyGlobalProperties();
    return this;
  };

  WenStormAnalytics.prototype.enable = function () {
    this._enabled = true;
    var q = this._queue; this._queue = [];
    for (var i = 0; i < q.length; i++) this._send(q[i][0], q[i][1]);
    return this;
  };

  WenStormAnalytics.prototype.disable = function () { this._enabled = false; this._queue = []; return this; };
  WenStormAnalytics.prototype.isEnabled = function () { return this._enabled; };

  WenStormAnalytics.prototype._send = function (name, props) {
    /* Stamped BEFORE the buffering check, deliberately. A queued event is replayed later, and
     * stamping on replay would attach whatever instance is current THEN rather than the one the
     * event actually happened on -- harmless while one document is one instance, wrong the moment
     * a route change can mint a second one. An explicit page_instance_id already in props (the
     * engagement snapshot carries its own) is never overwritten. */
    if (props && props.page_instance_id == null) props.page_instance_id = this._pageInstanceId;
    if (!this._enabled || !this._provider) {
      // Buffered rather than dropped: the provider may still be loading when the first page_viewed
      // fires, and losing the LANDING event would break exactly the journey this exists to record.
      if (this._queue.length < 50) this._queue.push([name, props]);
      return;
    }
    var merged = this.getContext();
    for (var k in props) if (props.hasOwnProperty(k)) merged[k] = props[k];
    /* A swallowed failure is how the stale-provider bug stayed invisible: nothing threw, events
     * simply went nowhere. Failures are still never allowed to break the storefront, but they are
     * now COUNTED. NOTE THE LIMIT OF WHAT THIS PROVES: it counts DISPATCH failures -- our call into
     * the provider returning false or throwing. It says nothing about whether PostHog's servers
     * ingested the event. A capture that returns cleanly and is then lost to a blocked network
     * still reports zero here. Named accordingly so nobody reads it as delivery confirmation. */
    try {
      var ok = this._provider.capture(name, merged);
      if (ok === false) this._failures++;
    } catch (e) { this._failures++; }
  };

  /* ---- the event vocabulary -------------------------------------------------------------------
   * Platform-neutral by design. No event here mentions Shopify, Liquid, a theme, or a section. */

  /* THE PAGE'S SECTIONS: ONE LIST, FROZEN, USED BY BOTH MEASUREMENTS.
   *
   * There used to be two independent reads of the document. `_outline()` collected h1-h3, dropped
   * the ones with no text and capped at 40. `PageEngagement._scan()` separately collected h1-h3 and
   * reported the raw NodeList index. Those two lists are not the same list, and the funnel reads
   * them as if they were: `page_outline[N]` is supposed to BE the heading that
   * `deepest_heading_index = N` refers to.
   *
   * With a single empty heading in the markup:
   *
   *     DOM            0 Portable EV Charger   1 (empty)   2 Charging Speed   3 Compatibility
   *     outline        0 Portable EV Charger               1 Charging Speed   2 Compatibility
   *     depth index    0                       1           2                  3
   *
   * a reader who stopped at "Charging Speed" is recorded as index 2, and index 2 of the outline is
   * "Compatibility". The chart then reports that people reached a section they never saw. Every
   * empty or hidden heading shifts everything below it.
   *
   * Three separate ways the two lists diverge, all fixed by having one list:
   *
   *   EMPTY HEADINGS      dropped by one collector and counted by the other
   *   HIDDEN HEADINGS     a display:none heading -- a collapsed drawer, the desktop copy of a
   *                       responsive pair, a modal -- has a bounding rect that can read as already
   *                       passed, so it is "reached" without ever having been on screen
   *   LATE DOM CHANGES    the outline is captured once at page_viewed; a heading inserted after
   *                       that renumbered every subsequent scan underneath the captured outline
   *
   * SCOPED TO THE PAGE'S CONTENT. Shopify's layout wraps the page body in `#MainContent`; headings
   * outside it are theme chrome -- header nav, footer, drawers -- and are not sections of the
   * document anybody reads. Falls back to the whole document when that container is absent, so a
   * different frontend still measures something sensible.
   *
   * FROZEN PER PAGE INSTANCE. Computed once, on first use, and reused for the life of the instance.
   * That is what makes the index stable: whatever the page does afterwards, index N means the same
   * heading it meant when the outline was sent. The cost is that a heading revealed later -- an
   * accordion opened by the reader -- is not counted. Measuring the page as it was presented is the
   * honest side of that trade; silently renumbering is not.
   */
  var MAX_TRACKED_HEADINGS = 40;

  function headingIsVisible(el) {
    try {
      // No boxes at all means display:none, or detached. Cheaper and more reliable than
      // offsetParent, which is also null for position:fixed elements that ARE visible.
      if (!el.isConnected || !el.getClientRects().length) return false;
      if (el.closest('[aria-hidden="true"]')) return false;
      var cs = (global.getComputedStyle ? global.getComputedStyle(el) : null);
      if (cs && (cs.visibility === 'hidden' || cs.display === 'none')) return false;
      return true;
    } catch (e) { return true; }   // never let a style query cost the measurement
  }

  WenStormAnalytics.prototype._trackedHeadings = function () {
    if (this._headings) return this._headings;
    var list = [];
    try {
      var root = document.querySelector('#MainContent') || document;
      var hs = root.querySelectorAll('h1, h2, h3');
      for (var i = 0; i < hs.length && list.length < MAX_TRACKED_HEADINGS; i++) {
        var text = (hs[i].textContent || '').trim().replace(/\s+/g, ' ').slice(0, 90);
        // THE CAP IS APPLIED AFTER FILTERING, so both consumers cap the same list at the same
        // point. Capping a raw NodeList and then filtering it produces two different lengths.
        if (text && headingIsVisible(hs[i])) list.push({ el: hs[i], text: text });
      }
    } catch (e) { list = []; }
    this._headings = list;
    return list;
  };

  /* THE PAGE'S OWN OUTLINE, sent once.
   *
   * The engagement record reports the deepest heading someone reached. On its own that is still not
   * a place: "section 3 of 7" tells you a count, not what the page is or what they skipped. The
   * whole outline turns it into a position -- you can see they stopped after the price list and
   * never reached the warranty section, which is a thing to act on.
   *
   * ONCE, on page_viewed, not on every engagement record. The outline is a property of the page,
   * and engagement records are cumulative and repeat; repeating a dozen headings on each of them
   * would multiply the payload for a value that cannot change while the page is open.
   */
  WenStormAnalytics.prototype._outline = function () {
    var tracked = this._trackedHeadings();
    if (!tracked.length) return null;
    var out = [];
    for (var i = 0; i < tracked.length; i++) out.push(tracked[i].text);
    return out;
  };

  WenStormAnalytics.prototype.pageViewed = function (extra) {
    var payload = extra || {};
    if (payload.page_outline === undefined) {
      var o = this._outline();
      if (o) payload.page_outline = o;
    }
    this._send('page_viewed', payload);
  };

  /* ---- page engagement ------------------------------------------------------------------------
   * MEASURED, NEVER INFERRED. This exists because the spike proved the native data cannot answer
   * the question: $prev_pageview_duration was NULL on every single $pageleave across four runs, so
   * the only native option is differencing timestamps -- which reports a visitor who opened a
   * thread, spent eighteen minutes in another tab, and came back, as eighteen minutes of reading.
   *
   * Three separate numbers, because they answer three different questions and collapsing them is
   * the whole failure mode:
   *
   *   elapsed_ms  wall clock from arrival to leaving. What timestamps already give you.
   *   visible_ms  time the page was actually the foreground tab (Page Visibility API).
   *   active_ms   visible AND recently interacted with. A page left open in the foreground while
   *               someone makes coffee is visible but not active.
   *
   * page_instance_id exists so a back-navigation to the same URL is a NEW measurement rather than
   * being merged with the earlier visit -- two separate readings of one page are two facts. It is
   * minted by startPageInstance() and carried by EVERY event on the page, not just this one.
   *
   * Platform-neutral by construction: visibilitychange and the interaction events used below are
   * DOM standards, not Shopify. A future frontend keeps this file unchanged.
   */
  var IDLE_AFTER_MS = 30000;   // no interaction for this long and the reader is no longer "active"

  function PageEngagement(analytics, instanceId) {
    this._a = analytics;
    this._id = instanceId;
    this._start = Date.now();
    this._visibleMs = 0;
    this._activeMs = 0;
    this._lastTick = Date.now();
    this._lastInteraction = Date.now();
    this._lastState = document.visibilityState;   // which state owns time right now
    this._final = false;      // a FINAL record has been emitted; nothing further may be sent
    this._seq = 0;            // how many records this instance has emitted, final included
    this._emittedVisible = -1;
    this._emittedActive = -1;
    this._maxScroll = 0;
    this._deepestIndex = -1;
    this._deepestHeading = null;
    this._sectionCount = 0;
  }

  PageEngagement.prototype.isFinal = function () { return this._final; };

  /* HOW FAR THEY GOT, AND WHAT THEY HAD REACHED WHEN THEY LEFT.
   *
   * A percentage on its own answers nothing a person can act on. "They left at 23%" of a guide
   * does not say whether they bailed before the price list or after it -- and 23% of a short page
   * and 23% of a long one are not comparable quantities. So the depth is recorded ALONGSIDE the
   * last heading they scrolled past, which is a thing the author recognises: "read as far as
   * 'How much does installation cost?'" is a sentence you can do something about.
   *
   * DEEPEST REACHED, not final position. Someone who reads to the bottom and scrolls back up to
   * the buy button has still read the whole thing, and reporting where their scrollbar happened to
   * rest at the end would say the opposite.
   *
   * Headings are h1-h3 because that is the shape of a document, not of this storefront -- nothing
   * here knows what a Shopify section is. */
  PageEngagement.prototype._scan = function () {
    var doc = document.documentElement;
    var scrollable = Math.max(1, (doc.scrollHeight || 0) - (window.innerHeight || 0));
    var y = window.pageYOffset || doc.scrollTop || 0;
    // Clamped: elastic scrolling on touch devices reports values past both ends.
    var pct = Math.max(0, Math.min(1, y / scrollable));
    if (pct > this._maxScroll) this._maxScroll = pct;

    // The last heading whose top has passed the middle of the viewport: the one they were most
    // plausibly reading, rather than one that merely peeked into view at the bottom.
    var mid = y + (window.innerHeight || 0) / 2;
    // THE SAME FROZEN LIST THE OUTLINE WAS BUILT FROM. Re-querying the document here is what made
    // deepest_heading_index refer to a different heading than page_outline[index]; see
    // _trackedHeadings().
    var hs = this._a._trackedHeadings();
    for (var i = 0; i < hs.length; i++) {
      var el = hs[i].el;
      // A heading removed from the document after the list was frozen reports a zero rect, which
      // would read as "at the very top, therefore passed". Skip it rather than count it.
      if (!el.isConnected || !el.getClientRects().length) continue;
      var top = el.getBoundingClientRect().top + y;
      if (top <= mid && i >= this._deepestIndex) {
        this._deepestIndex = i;
        this._deepestHeading = hs[i].text;
      }
    }
    this._sectionCount = hs.length;
  };



  /* THE SLICE BELONGS TO THE STATE THAT OWNED IT.
   *
   * This used to judge the whole `now - lastTick` interval by document.visibilityState READ AT THE
   * END of it. A visibilitychange event fires AFTER the state has already flipped, so on every
   * transition the reading was of the new state, and the interval leading up to it was filed under
   * the wrong one. Both directions were wrong:
   *
   *   hidden -> visible   a hidden gap short enough to survive the 5s guard (say 3s with no timer
   *                       tick, which is exactly what a throttled background tab produces) was
   *                       credited as VISIBLE, and as ACTIVE too. Measured: 1s visible + 3s hidden
   *                       reported as 4s visible, 4s active.
   *   visible -> hidden   the slice of genuine reading immediately before the switch was filed as
   *                       hidden and silently dropped.
   *
   * So the previous state is carried explicitly. The delta is attributed to the state that was in
   * force while it elapsed, and only then does the new state take ownership of time going forward. */
  PageEngagement.prototype._accrue = function () {
    var now = Date.now();
    var delta = now - this._lastTick;
    this._lastTick = now;
    var owner = this._lastState;                 // the state that actually held this slice
    this._lastState = document.visibilityState;  // from here on, the new state owns time
    if (delta <= 0) return;
    // Never accrue across a suspended tab: a throttled timer can return a huge delta that did not
    // happen in front of a human. Anything longer than a few ticks is discarded rather than trusted.
    if (delta > 5000) return;
    if (owner === 'visible') {
      this._visibleMs += delta;
      if (now - this._lastInteraction < IDLE_AFTER_MS) this._activeMs += delta;
    }
  };

  PageEngagement.prototype.snapshot = function () {
    this._accrue();
    this._scan();
    return {
      page_instance_id: this._id,
      elapsed_ms: Date.now() - this._start,
      visible_ms: Math.round(this._visibleMs),
      active_ms: Math.round(this._activeMs),
      // How far down they got, as a fraction, and the heading that depth corresponds to.
      max_scroll: Math.round(this._maxScroll * 100) / 100,
      deepest_heading: this._deepestHeading,
      deepest_heading_index: this._deepestIndex >= 0 ? this._deepestIndex : null,
      heading_count: this._sectionCount || null,
    };
  };

  /* THE LIFECYCLE, AND THE BUG IT REPLACES.
   *
   * The previous version had ONE emit path, flush(), guarded by a one-shot `_sent` flag, and wired
   * visibilitychange->hidden straight into it. So the first time a visitor backgrounded the tab,
   * the page was finalized: the record that reached PostHog held only the time accrued BEFORE the
   * switch, and the pagehide flush carrying the complete numbers returned early and sent nothing.
   *
   *   read 30s -> switch tabs (record sent: 30s) -> return -> read 60s more -> leave (SILENT)
   *
   * Reported: 30s. True: 90s. And the visitor who leaves and comes back is precisely the visitor
   * this tracker was built to measure, so the failure was aimed at its own use case.
   *
   * Backgrounding is now a CHECKPOINT, not a terminal event. Checkpoints are repeatable and carry
   * is_final:false; only pagehide (or an explicit page-instance replacement) finalizes.
   *
   * Every record is CUMULATIVE from the start of the page instance, never a delta, so the reader
   * needs no arithmetic and no ordering guarantee: for a given page_instance_id, take the row with
   * the highest `sequence` -- preferring is_final when present -- and discard the rest. That rule
   * is also what makes checkpoints safe to lose. A mobile tab discarded without ever firing
   * pagehide still leaves its last checkpoint standing as the best available measurement, which is
   * the reason for emitting them at all. */
  PageEngagement.prototype._emit = function (reason, isFinal) {
    var d = this.snapshot();
    this._seq += 1;
    d.reason = reason || 'unknown';
    d.is_final = !!isFinal;
    d.sequence = this._seq;
    this._emittedVisible = d.visible_ms;
    this._emittedActive = d.active_ms;
    this._a._send('wenstorm_page_engagement', d);
    return true;
  };

  /* Repeatable. Skipped when nothing has been measured since the last record, so a visitor flipping
   * between two tabs does not mint a row per flip -- those rows would be identical and carry no
   * information. */
  PageEngagement.prototype.checkpoint = function (reason) {
    if (this._final) return false;
    this._accrue();
    var v = Math.round(this._visibleMs), a = Math.round(this._activeMs);
    if (v === this._emittedVisible && a === this._emittedActive) return false;
    return this._emit(reason || 'checkpoint', false);
  };

  /* Terminal, and genuinely once. */
  PageEngagement.prototype.finalize = function (reason) {
    if (this._final) return false;
    this._final = true;
    return this._emit(reason || 'final', true);
  };

  /* ONE SET OF LISTENERS PER DOCUMENT, EVER.
   *
   * These used to be installed inside startEngagement(), each closing over the tracker that existed
   * at that moment. startEngagement() is called again on a bfcache restore, so every browser Back
   * that restored this document added a SECOND live set bound to a new tracker while the first set
   * stayed subscribed. Measured on this code: 1 -> 2 -> 4 pagehide/pageshow handlers and timers
   * across two restores, and the second restore fired two pageshow handlers, minting two page
   * instances and two page_viewed events for one Back press. Tracking that quietly clones itself
   * is worse than tracking that fails, because the extra rows look like real visitor behaviour.
   *
   * The fix is not to remove listeners on teardown -- that is a bookkeeping exercise with its own
   * leak -- but to make them stateless with respect to the tracker: they are installed once and
   * resolve the CURRENT engagement at call time, exactly like the PostHog provider resolves
   * window.posthog at call time rather than holding the loader stub. Same bug, same shape, same
   * fix. A finalized instance is skipped, so the handlers idle harmlessly between instances. */
  WenStormAnalytics.prototype._installLifecycle = function () {
    if (this._lifecycleInstalled) return;
    this._lifecycleInstalled = true;
    var self = this;
    var live = function () {
      var e = self._engagement;
      return e && !e.isFinal() ? e : null;
    };

    /* Accrue on the way in, THEN record the interaction. Order matters: the slice of time being
     * banked here happened BEFORE this interaction, so it must be judged against the previous one.
     *
     * Interactions drive accrual as well as the timer because the timer alone is not dependable.
     * Chrome throttles setInterval in a tab it considers background -- measured at 8.0s between
     * ticks during a live test -- and _accrue() discards any tick longer than 5s as a suspended
     * tab. A page whose only accrual path was the interval would therefore under-report whenever
     * the browser decided to throttle it. Someone scrolling and typing is unambiguously reading,
     * and their own events are the most trustworthy clock available. */
    var mark = function () {
      var e = live(); if (!e) return;
      e._accrue(); e._lastInteraction = Date.now();
    };
    ['mousedown', 'keydown', 'wheel', 'touchstart', 'scroll'].forEach(function (evt) {
      document.addEventListener(evt, mark, { passive: true, capture: true });
    });
    // Scrolling also advances the depth mark. Cheap and throttled to one measurement per frame, so
    // a fast scroll costs one scan rather than one per pixel.
    var scanQueued = false;
    document.addEventListener('scroll', function () {
      var e = live(); if (!e || scanQueued) return;
      scanQueued = true;
      (window.requestAnimationFrame || window.setTimeout)(function () { scanQueued = false; e._scan(); }, 16);
    }, { passive: true, capture: true });

    document.addEventListener('visibilitychange', function () {
      var e = live(); if (!e) return;
      e._accrue();   // attributes the slice to the state that owned it, then hands over
      if (document.visibilityState === 'visible') e._lastInteraction = Date.now();
      // Backgrounding: record what we have, keep measuring. NOT a finalization.
      else e.checkpoint('hidden');
    });

    // A tick keeps the accrual honest even with no events at all, and bounds the damage from any
    // single missed transition.
    setInterval(function () { var e = live(); if (e) e._accrue(); }, 1000);

    // sendBeacon semantics matter here: this fires as the page is going away. This is the ONLY
    // terminal transition -- the one place where "no more time will be spent here" is actually true.
    window.addEventListener('pagehide', function () {
      var e = live(); if (e) e.finalize('pagehide');
    });

    /* Restored from the back/forward cache: the document was NOT rebuilt, so nothing above runs
     * again, and pagehide has already finalized the previous instance. Without this the second
     * viewing of the page is measured as zero. A restore is a new visit to the same URL, which is
     * the case page_instance_id exists for, so it gets a new instance and a new page_viewed marked
     * as a restore rather than being folded into the first reading. */
    window.addEventListener('pageshow', function (e) {
      if (!e || !e.persisted) return;
      self.startPageInstance();
      self.startEngagement();
      self.pageViewed({ restored_from_bfcache: true });
    });
  };

  WenStormAnalytics.prototype.startEngagement = function () {
    // Idempotent while an instance is live: a second bootstrap must not open a second tracker on
    // the same page, which would emit two competing records for one visit.
    if (this._engagement && !this._engagement.isFinal()) return this._engagement;
    if (!this._pageInstanceId) this.startPageInstance();
    this._engagement = new PageEngagement(this, this._pageInstanceId);
    this._installLifecycle();   // no-op after the first call, however many instances follow
    return this._engagement;
  };

  WenStormAnalytics.prototype.engagement = function () { return this._engagement || null; };

  WenStormAnalytics.prototype.productClicked = function (p) {
    this._send('product_clicked', {
      product_id: p && p.product_id != null ? String(p.product_id) : null,
      product_handle: (p && p.product_handle) || null,
      product_title: (p && p.product_title) || null,
      // Where the click came FROM, which is the whole point of a journey: a product clicked out of a
      // forum thread is a different fact from the same product clicked on a collection page.
      from_page_type: this._context.page_type,
      from_entity_id: this._context.entity_id,
      // WHERE on the page it was clicked. The same destination reached from the header nav and from
      // a hero call-to-action are different facts, and the difference is not recoverable later.
      click_location: (p && p.click_location) || null,
    });
  };

  WenStormAnalytics.prototype.forumThreadClicked = function (t) {
    this._send('forum_thread_clicked', {
      thread_id: t && t.thread_id != null ? String(t.thread_id) : null,
      // Kept ALONGSIDE the id, never instead of it: a handle is readable in a report but can be
      // changed by an edit, so it is a label and the id is the identity.
      thread_handle: (t && t.thread_handle) || null,
      thread_title: (t && t.thread_title) || null,
      from_page_type: this._context.page_type,
      click_location: (t && t.click_location) || null,
    });
  };

  /* ORDINARY MOVEMENT AROUND THE SITE. Not every click deserves a name of its own, but every one
   * of them is part of how somebody got where they went -- and WHERE on the page they clicked is
   * the part that cannot be recovered afterwards. A link to the forum from the top nav and the same
   * link in the footer are two different facts about what is working on that page. */
  WenStormAnalytics.prototype.navigationClicked = function (n) {
    this._send('navigation_clicked', {
      target_path: (n && n.target_path) || null,
      target_label: (n && n.target_label) || null,
      click_location: (n && n.click_location) || null,
      from_page_type: this._context.page_type,
      from_entity_id: this._context.entity_id,
    });
  };

  WenStormAnalytics.prototype.cartUpdated = function (c) {
    this._send('cart_updated', {
      item_count: c && c.item_count != null ? Number(c.item_count) : null,
      // Deliberately no line-item detail and no prices here. This spike is about NAVIGATION; revenue
      // belongs to commerce reporting, and adding it now would widen what we collect for no gain.
      from_page_type: this._context.page_type,
    });
  };

  WenStormAnalytics.prototype.actionClicked = function (a) {
    this._send('action_clicked', {
      action: (a && a.action) || null,          // e.g. 'add_to_cart', 'checkout', 'specifications'
      label: (a && a.label) || null,            // the human-readable text, for reading reports
      // An action has no href and no durable id, so it carries a fingerprint of what was clicked
      // instead -- the tag and the visible text, which is what a raw interaction row also records.
      target_tag: (a && a.target_tag) || null,
      // How the action was identified: null when the control announced itself, otherwise the
      // evidence it was deduced from. A deduced action is still an action, but a reader is
      // entitled to know which kind they are looking at.
      detected_via: (a && a.detected_via) || null,
      click_location: (a && a.click_location) || null,
      from_page_type: this._context.page_type,
      from_entity_id: this._context.entity_id,
    });
  };

  WenStormAnalytics.PAGE_TYPES = PAGE_TYPES;
  global.WenStormAnalytics = WenStormAnalytics;
  global.wenstormAnalytics = global.wenstormAnalytics || new WenStormAnalytics();
})(window);
